import _ from 'lodash';
import $router from '../router';
import { AuthService } from '@/services/auth.service';

/**
 * Return message for HTTP status code
 * @param {number} status - HTTP status code
 * @returns {string} Message of network operation
 */
function _getStatusMessage (status) {
  let message = '';
  switch (status) {
    case 200:
      message = 'All done. Request successfully executed';
      break;
    case 201:
      message = 'Data successfully created';
      break;
    case 400:
      message = 'Bad Request';
      break;
    case 401:
      message = 'Need auth';
      break;
    case 404:
      message = 'Not found';
      break;
    case 503:
      message = 'Service unavailable. Try again later';
      break;
    default:
      message = 'Something wrong. Client default error message';
      break;
  }
  
  return message;
}

function _getResponseErrorMessage (error) {
  if (error.response && error.response.data) {
    if (error.response.data.message) {
      return error.response.data.message;
    } else {
      return error.response.data;
    }
  }

  if (error.response && error.response.statusText) {
    return error.response.statusText;
  }
  
  return error.message === 'Network Error'
    ? 'Oops! Network Error. Try again later'
    : error.message;
}

/**
 * Create instant, which represent response object
 * @param {Object} [data] - custom data
 * @param {Object} [response] - axios response object
 * @param {String} [message] - custom message to display
 */
export class ResponseWrapper {
  constructor (response, data = {}, message) {
    this.data = data;
    this.success = response.data?.success || false;
    this.status = response.status;
    this.statusMessage = _getStatusMessage(this.status);
    this.message = message || null;
  }
}

/**
 * Create instant, which represent error object
 * @param {Object} [error] - axios error object
 * @param {String} [message] - custom message to display
 */
export class ErrorWrapper extends Error {
  constructor (error, message) {
    super();
    this.data = error.response?.data;
    this.success = error.response ? error.response.data.success : false;
    this.meta = error.response ? error.response.data.meta : false;
    this.code = error.response ? error.response.data.code : false;
    this.status = error.response ? error.response.status : false;
    this.statusMessage = _getStatusMessage(this.status);
    this.message = message || _getResponseErrorMessage(error);

    // Automatic login redirection
    if (this.message == 'Invalid token') {
      AuthService.logout();
      $router.push('/login?expired=true');
    }
  }
}

/**
 * Uses to clear request data before send it
 * Client shouldn't change entity id
 * @param data
 * @return {{}}
 */
export function clearData (data) {
  const result = {};
  _.forEach(data, (item, propName) => {
    if (_.isArray(item) && item.length) {
      result[propName] = item;
    }
    if (!_.isArray(item) && item && propName !== 'id') {
      result[propName] = item;
    }
  });
  
  return result;
}

// Format distance (date-fns)
const formatDistanceLocale = {
  lessThanXSeconds: '1m',
  xSeconds: '1m',
  halfAMinute: '1m',
  lessThanXMinutes: '{{count}}m',
  xMinutes: '{{count}}m',
  aboutXHours: '{{count}}h',
  xHours: '{{count}}h',
  xDays: '{{count}}d',
  aboutXWeeks: '{{count}}w',
  xWeeks: '{{count}}w',
  aboutXMonths: '{{count}}mo',
  xMonths: '{{count}}mo',
  aboutXYears: '{{count}}y',
  xYears: '{{count}}y',
  overXYears: '{{count}}y',
  almostXYears: '{{count}}y',
};

export function formatDistance (token, count, options) {
  options = options || {};

  const result = formatDistanceLocale[token].replace('{{count}}', count);

  if (options.addSuffix) {
    return result;
  }

  return result;
}
